import React, { useEffect, useMemo, useState } from "react";
import Header from "@features/ux/components/Header";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Badge } from "@shared/ui/badge";
import { Eye, FilterX, X, ArrowLeft } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@shared/ui/select";
import { Button } from "@shared/ui/button";
import { useNotificationChoices } from "@features/ux/hooks";
import { useToast } from "@shared/ui/use-toast";
import {
  useCancelScheduledNotification,
  useScheduledNotificationDetails, // ✅ UPDATED: Use only this hook
} from "@features/ux/history/hooks";

const getBadgeClass = (type: "status" | "channel" | "type", value: string) => {
  const baseClasses = {
    default: "bg-muted text-muted-foreground",
    // Channel types
    email: "bg-blue-50 text-blue-600",
    sms: "bg-green-50 text-green-600",
    push: "bg-purple-50 text-purple-600",
    // Status types
    completed: "bg-green-50 text-green-600",
    scheduled: "bg-blue-50 text-blue-600",
    failed: "bg-red-50 text-red-600",
    pending: "bg-yellow-50 text-yellow-600",
    // Type of
    bulk: "bg-gray-900 text-white",
    individual: "bg-blue-50 text-blue-600",
  };
  return baseClasses[value?.toLowerCase()] || baseClasses.default;
};

const getInitialFilters = (params: URLSearchParams) => ({
  type: params.get("type") || "",
  order: params.get("order") || "",
});

// Helper function to format recipient info
const formatRecipientInfo = (
  recipientName: string,
  recipientsCount: number
) => {
  if (!recipientName) return "NA";

  if (recipientsCount <= 1) {
    return recipientName;
  }

  return `${recipientName} (${recipientsCount} recipients)`;
};

// Custom recipient cell component
const RecipientCell = ({ recipientName, recipientsCount }) => {
  const displayText = formatRecipientInfo(recipientName, recipientsCount);

  return <span title={displayText}>{displayText}</span>;
};

// Custom type cell component with icon
const TypeCell = ({ typeOf }) => {
  return (
    <Badge className={getBadgeClass("type", typeOf)}>
      <span className="mr-1">
        {typeOf?.toLowerCase() === "bulk" ? "📧" : "👤"}
      </span>
      {typeOf?.toUpperCase()}
    </Badge>
  );
};

const ScheduledMessagesHistory = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  const [pendingFilters, setPendingFilters] = useState(
    getInitialFilters(searchParams)
  );
  const search = searchParams.get("search") || "";
  const appliedFilters = getInitialFilters(searchParams);

  // Hooks for choices
  const { data: typeChoices } = useNotificationChoices("type_of");

  // ✅ UPDATED: Use only scheduled notification details hook
  const {
    data: scheduledNotifications,
    isLoading,
    refetch,
  } = useScheduledNotificationDetails({
    remoteUtilityId,
    search,
    page: currentPage,
    type: appliedFilters.type,
    order: appliedFilters.order,
  });

  const cancelMutation = useCancelScheduledNotification();

  const processedData = useMemo(() => {
    if (!scheduledNotifications?.results) return [];

    // Just map the results since filtering is now done server-side
    return scheduledNotifications.results.map((item) => ({
      ...item,
      id: item?.id,
      message: item?.template?.name || item?.name || "No template",
      recipientName: item?.recipientList?.name || "Unknown",
      recipientsCount: item?.recipientList?.totalRecipients || 0,
      channel: item?.channelType || "N/A",
      status: item?.status || "Scheduled",
      scheduledDateTime: `${item?.scheduledDate} ${item?.scheduledTime}`,
      typeOf: item?.type || "N/A",
    }));
  }, [scheduledNotifications]);

  const handleCancelNotification = async (notificationId: number) => {
    if (
      window.confirm(
        "Are you sure you want to cancel this scheduled notification?"
      )
    ) {
      try {
        await cancelMutation.mutateAsync(notificationId);
        toast({
          title: "Success",
          description: "Notification cancelled successfully",
        });
        refetch(); // Refresh the table
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to cancel notification",
          variant: "destructive",
        });
      }
    }
  };

  const handleViewNotification = (row: any) => {
    navigate(`/communication-hub/scheduled-messages/scheduled/${row}`);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "typeOf",
      header: "TYPE",
      type: "custom",
      render: (typeOf) => <TypeCell typeOf={typeOf} />,
    },
    {
      key: "message",
      header: "MESSAGE",
    },
    {
      key: "recipientName",
      header: "RECIPIENT",
      type: "custom",
      render: (_, row) => (
        <RecipientCell
          recipientName={row.recipientName}
          recipientsCount={row.recipientsCount}
        />
      ),
    },
    {
      key: "channel",
      header: "CHANNEL",
      type: "badge",
      badgeConfig: {
        getClassName: (value) => getBadgeClass("channel", value),
      },
    },
    {
      key: "status",
      header: "STATUS",
      type: "badge",
      badgeConfig: {
        getClassName: (value) => getBadgeClass("status", value),
      },
    },
    {
      key: "scheduledDateTime",
      header: "SCHEDULED FOR",
    },
    {
      key: "tableActions",
      header: "ACTIONS",
      width: "w-32",
      type: "custom",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {/* <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewNotification(row.id)}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button> */}
          {row.status?.toLowerCase() === "scheduled" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCancelNotification(row.id)}
              title="Cancel"
              disabled={cancelMutation.isPending}
              className="text-red-600 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleApplyFilters = () => {
    const newParams: Record<string, string> = {
      ...(pendingFilters.type && { type: pendingFilters.type }),
      ...(pendingFilters.order && { order: pendingFilters.order }),
    };
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    setPendingFilters({ type: "", order: "" });
    const newParams = Object.fromEntries(searchParams.entries());
    ["type", "order", "search", "page"].forEach((key) => delete newParams[key]);
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams = {
      ...Object.fromEntries(searchParams.entries()),
      search: searchTerm,
    };
    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  useEffect(() => {
    setPendingFilters(getInitialFilters(searchParams));
  }, [searchParams]);

  const pagination = useMemo(() => {
    const count = scheduledNotifications?.count || 0;
    const itemsPerPage = 10;
    const totalPages = Math.ceil(count / itemsPerPage);
    return scheduledNotifications
      ? {
          currentPage,
          totalPages,
          totalItems: count,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(currentPage * itemsPerPage, count),
          itemsPerPage,
        }
      : undefined;
  }, [scheduledNotifications, currentPage]);

  const renderSelectFilter = (
    label: string,
    field: keyof typeof pendingFilters,
    options: { key: string; value: string }[],
    placeholder = `Select ${label}`
  ) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <Select
        value={pendingFilters[field]}
        onValueChange={(value) =>
          setPendingFilters((prev) => ({ ...prev, [field]: value }))
        }
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.key} value={String(opt.key)}>
              {opt.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const filterPanel = useMemo(
    () => (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderSelectFilter(
            "Type",
            "type",
            typeChoices || [
              { key: "bulk", value: "Bulk" },
              { key: "individual", value: "Individual" },
            ]
          )}
          {renderSelectFilter("Sort By", "order", [
            { key: "-scheduled_date", value: "Newest" },
            { key: "scheduled_date", value: "Oldest" },
          ])}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="default" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
          <Button variant="ghost" onClick={handleResetFilters}>
            <FilterX className="w-4 h-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>
    ),
    [pendingFilters, typeChoices]
  );

  const hasActiveFilters = pendingFilters.type || pendingFilters.order;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <Header
        title="Scheduled Messages"
        subtext="View and manage your scheduled messages. Cancel messages that haven't been sent yet."
      />
      <Button
          variant="ghost"
          onClick={() => navigate("/communication-hub/messaging")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Messaging
        </Button>
      </div>

      

      <GenericTable
        data={processedData}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by template name or recipient"
        emptyMessage="No scheduled messages found"
      />
    </div>
  );
};

export default ScheduledMessagesHistory;
